import React, { useState, useEffect } from "react";
import Header from "components/Header";
import RightSidebar from "./RightSidebar";
import ProjectGlobalSidebar from "../Project Dashboard/ProjectGlobalSidebar";
import MainContent from "./MainContent";
import { useLocation, useNavigate, Outlet } from "react-router-dom";
import NewSiteForm from "./NewSiteForm";
import AddNewMember from "./AddNewMember";
import { BASE_URL } from "../../utils/constant";
import { X } from "lucide-react";
import OrganizationDetails from "./OrganizationOwner";
import { AuthManager } from "utils/authUtils";
import Projects from "./Projects/Projects";
import Vendors from "./Vendors/Vendors";
import Finance from "./Finance/newfinance";
import OrganizationTeam from "./Organization Team/OrganizationTeam";
import ComingSoonCard from "components/ComingSoon";
import RaiseRequest from "./raise-ticket/RaiseRequest";
import Procurement from "./Procurement/Procurement";
import NotificationPopup from "components/NotificationPopup";
import { useQuery } from "@tanstack/react-query";
import { SubscriptionAPI } from "api/dashboard/service";
import Chats from "./Global Chat/Chats";
// import Quotationgenerator from "./Quotation-generator/Quotationgenerator";
import LeadManagement from "./Lead Management/LeadManagement";
import Design from "./Design/Design";
import GlobalMedia from "./Global Media/GlobalMedia";
import ContractorManagement from "./New Contractor/Contractor";
import Settings from "./Settings/Settings";
import HelpPage from "./helps/helpcenter";
import GlobalReport from "./GlobalReport/GlobalReport";
import PettyCashManagement from "./Petty Cash/PettyCashManagement";
import Warehouse from "./Warehouse/Warehouse";
import SnagsManagement from "./Snags/SnagsManagement";
import OwnerDashboard from "./OwnerDashboard";
import IndividualDashboard from "./IndividualDashboard";
import Manage from "./Reminders/Manage";

const authManager = new AuthManager(BASE_URL);

const GLOBAL_SIDEBAR_STORAGE_KEY = "projectGlobalSidebarCollapsed";

const hasStoredOrganization = () => {
  try {
    const stored = JSON.parse(localStorage.getItem("organization") || "null");
    return Boolean(stored?.id);
  } catch {
    return false;
  }
};

const getStoredOrganization = () => {
  try {
    return JSON.parse(localStorage.getItem("organization") || "null");
  } catch {
    return null;
  }
};

const getSelectedOrganization = (organizations) => {
  const stored = getStoredOrganization();
  if (!stored?.id) return organizations[0];
  return organizations.find((org) => org?.id === stored.id) || organizations[0];
};

const getStoredGlobalSidebarCollapsed = () => {
  try {
    const saved = localStorage.getItem(GLOBAL_SIDEBAR_STORAGE_KEY);
    if (saved === null) return true;
    return saved === "true";
  } catch {
    return true;
  }
};

const Modal = ({ isOpen, onClose, children }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 ">
      <div
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={onClose}
      />
      <div
        className="relative w-full mx-4"
        style={{
          overflowY: "auto",
        }}
      >
        {children}
      </div>
    </div>
  );
};

function Dashboard({ active, flush, todayMs, prepareForLogout }) {
  const [filteredSites, setFilteredSites] = useState([]);
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);
  const [showNewSiteForm, setShowNewSiteForm] = useState(false);
  const [showInvitationsForm, setShowInvitationsForm] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    getStoredGlobalSidebarCollapsed,
  );

  const [organization, setOrganization] = useState(
    JSON.parse(localStorage.getItem("organization")) || {},
  );
  const [isLoading, setIsLoading] = useState(() => !hasStoredOrganization());
  const [error, setError] = useState(null);
  const [showAddNewMember, setShowAddNewMember] = useState(false);
  const [showOrganizationDetails, setShowOrganizationDetails] = useState(false);
  const [isOwner, setIsOwner] = useState(false);
  const [hasNoOrganization, setHasNoOrganization] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [isValidSubscription, setIsValidSubscription] = useState(false);
  const [currentMember, setCurrentMember] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const isContractorDetail = /\/dashboard\/contractor\/.+/.test(
    location.pathname,
  );

  // Get current tab from URL
  const getCurrentTabFromPath = (path) => {
    if (path.includes("/dashboard/home")) return "Dashboard";
    if (path.includes("/dashboard/projects")) return "Projects";
    if (path.includes("/dashboard/procurement")) return "Procurement";
    if (path.includes("/dashboard/warehouse")) return "Warehouse";
    if (path.includes("/dashboard/vendor")) return "Vendor";
    if (path.includes("/dashboard/contractor")) return "Contractor";
    if (path.includes("/dashboard/finance")) return "Finance";
    if (path.includes("/dashboard/organization-team"))
      return "Organization Team";
    if (path.includes("/dashboard/organization")) return "Organization";
    if (path.includes("/dashboard/document-manager")) return "Document Manager";
    if (path.includes("/dashboard/quotation-generator"))
      return "Quotation Generator";
    if (path.includes("/dashboard/lead-management")) return "Lead Management";
    if (path.includes("/dashboard/design")) return "Design";
    if (path.includes("/dashboard/raise-request")) return "Raise Request";
    if (path.includes("/dashboard/petty-cash")) return "Petty Cash";
    if (path.includes("/dashboard/help")) return "Help";
    if (path.includes("/dashboard/snags")) return "Snags";
    if (path.includes("/dashboard/actions")) return "Actions";
    if (path.includes("/dashboard/global-communication"))
      return "Global Communication";
    if (path.includes("/dashboard/settings")) return "Settings";
    if (path.includes("/dashboard/global-media")) return "Global Media";
    if (path.includes("/dashboard/global-report")) return "Global Report";
    return "Dashboard"; // default
  };

  const currentTab = React.useMemo(
    () => getCurrentTabFromPath(location.pathname),
    [location.pathname],
  );

  // Function to handle tab changes
  const handleTabChange = (tabName) => {
    const routeMap = {
      Dashboard: "/dashboard/home",
      Projects: "/dashboard/projects",
      Procurement: "/dashboard/procurement",
      Warehouse: "/dashboard/warehouse",
      Vendor: "/dashboard/vendor",
      Contractor: "/dashboard/contractor",
      Finance: "/dashboard/finance",
      Snags: "/dashboard/snags",
      "Organization Team": "/dashboard/organization-team",
      "Document Manager": "/dashboard/document-manager",
      "Quotation Generator": "/dashboard/quotation-generator",
      "Lead Management": "/dashboard/lead-management",
      "Actions": "/dashboard/actions",
      Design: "/dashboard/design",
      "Petty Cash": "/dashboard/petty-cash",
      Settings: "/dashboard/settings",
      "Raise Request": "/dashboard/raise-request",
      Help: "/dashboard/help",
      "Global Report": "/dashboard/global-report",
      "Global Communication": "/dashboard/global-communication",
      "Global Media": "/dashboard/global-media",
      Organization: "/dashboard/organization",
    };

    if (routeMap[tabName]) {
      navigate(routeMap[tabName]);
    }
  };

  async function checkAuth() {
    const result = await authManager.initialize();

    if (result.status) {
      // User is authenticated
      console.log("User is authenticated");
    } else {
      // Handle authentication failure
      navigate("/login");
      console.log("Authentication failed:", result.error);
    }
  }
  async function getOwnerOrganization() {
    const shouldBlockLoading = !hasStoredOrganization();
    try {
      if (shouldBlockLoading) {
        setIsLoading(true);
      }
      // checkAuth();
      setError(null);
      const accessToken = localStorage.getItem("access");
      const response = await fetch(
        `${BASE_URL}/org/list-users-created-organizations/`,
        {
          method: "GET",
          headers: new Headers({
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          }),
        },
      );

      if (!response.ok) {
        // if (response.status === 401) {
        //   localStorage.removeItem("access");
        //   localStorage.removeItem("refresh");
        //   navigate("/login");
        //   return;
        // }
        throw new Error("Failed to fetch organization data");
      }

      const data = await response.json();

      if (data?.status) {
        if (data?.data.length === 0) {
          setOrganization({});
          setCurrentMember(null);
          setHasNoOrganization(true);
          localStorage.removeItem("organization");
          window.dispatchEvent(new Event("organizationChanged"));
          return;
        }
        const selectedOrganization = getSelectedOrganization(data.data);
        setHasNoOrganization(false);
        setOrganization(selectedOrganization);
        localStorage.setItem("organization", JSON.stringify(selectedOrganization));
        window.dispatchEvent(new Event("organizationChanged"));
      } else {
        throw new Error(data?.message || "Failed to load organization data");
      }
    } catch (error) {
      setError(error.message);
      console.error("Dashboard error:", error);
    } finally {
      if (shouldBlockLoading) {
        setIsLoading(false);
      }
    }
  }

  const fetchOrganizations = async () => {
    const shouldBlockLoading = !hasStoredOrganization();
    try {
      if (shouldBlockLoading) {
        setIsLoading(true);
      }
      setError(null);
      const accessToken = localStorage.getItem("access");
      // if (!accessToken) {
      //   navigate("/login");
      //   return;
      // }

      const response = await fetch(`${BASE_URL}/org/list-user-organization/`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      });
      const data = await response.json();

      if (data.status) {
        if (data.data.length > 0) {
          const selectedOrganization = getSelectedOrganization(data.data);
          setHasNoOrganization(false);
          setOrganization(selectedOrganization);
          localStorage.setItem("organization", JSON.stringify(selectedOrganization));
          window.dispatchEvent(new Event("organizationChanged"));
        } else {
          setOrganization({});
          setCurrentMember(null);
          setHasNoOrganization(true);
          localStorage.removeItem("organization");
          window.dispatchEvent(new Event("organizationChanged"));
        }
      }
    } catch (error) {
      console.error("Error fetching organizations:", error);
    } finally {
      if (shouldBlockLoading) {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    // Only run this logic on mount or if specific props change that *require* re-verification.
    // We avoid listing localStorage.getItem(...) in dependencies as it is not a reactive source.

    const isOwnerStored = localStorage.getItem("isOwner") === "true";
    const hasOrgStored = !!localStorage.getItem("organization");

    setIsOwner(isOwnerStored);

    if (isOwnerStored) {
      getOwnerOrganization();
    } else {
      // Always verify associations so stale localStorage cannot hide the empty state.
      fetchOrganizations();
      if (hasOrgStored) {
        setIsLoading(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFilterApply = (newFilteredSites) => {
    setFilteredSites(newFilteredSites);
  };

  const handleNewProjectClick = () => {
    setShowNewSiteForm(true);
  };

  const handleInvitationsClick = () => {
    setShowInvitationsForm(true);
  };

  const handleCloseNewSiteForm = () => {
    setShowNewSiteForm(false);
  };
  const handleDashboardClick = () => {
    setShowOrganizationDetails(false);
  };
  const handleCloseInvitationsForm = () => {
    setShowInvitationsForm(false);
  };

  const onOrganizationClick = (org) => {
    localStorage.setItem("organization", JSON.stringify(org));
    setOrganization(org);
  };

  const handleOrganizationDetailsClick = () => {
    setShowOrganizationDetails(true);
  };

  const fetchSubscription = async (organizationId) => {
    try {
      const data = await SubscriptionAPI.checkUserSubscription(organizationId);
      if (data.status) {
        setSubscription(data.data);
        const hasActiveSubscription =
          data?.status &&
          data.data?.active_flag &&
          data.data?.valid_till > new Date().toISOString();
        setIsValidSubscription(hasActiveSubscription);
      }
    } catch (error) {
      console.error("Error fetching subscription:", error);
    }
  };
  useEffect(() => {
    if (organization?.id) {
      fetchSubscription(organization.id);
    }
  }, [organization]);

  const fetchCurrentMember = async (organizationId) => {
    setPermissionsLoaded(false);
    const user = JSON.parse(localStorage.getItem("user"));
    try {
      const accessToken = localStorage.getItem("access");
      const response = await fetch(
        `${BASE_URL}/org/organization/${organizationId}/members/`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );
      const data = await response.json();

      if (data.status) {
        setCurrentMember(data.data.find((m) => m.user.id === user.user));
      }
    } catch (error) {
      console.error("Error fetching current member:", error);
    } finally {
      setPermissionsLoaded(true);
    }
  };
  useEffect(() => {
    if (organization?.id) {
      fetchCurrentMember(organization.id);
    } else {
      setCurrentMember(null);
      setPermissionsLoaded(true);
    }
  }, [organization]);

  const canAccessDashboard =
    isOwner ||
    currentMember?.is_admin === true ||
    (currentMember?.joined_as || "").toLowerCase() === "owner";

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-900"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-red-600 text-center">
          <p className="text-xl font-semibold">Error loading dashboard</p>
          <p className="mt-2">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 px-4 py-2 bg-purple-900 text-white rounded hover:bg-purple-800"
          >
            Retry
          </button>
          <button
            onClick={() => {
              localStorage.clear();
              navigate("/login");
            }}
            className="mt-2 px-4 py-2 bg-gray-200 text-purple-900 rounded hover:bg-gray-300"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`relative flex h-screen flex-col bg-sm-background transition-[padding] duration-200 ease-in-out ${
        sidebarCollapsed ? "pl-16" : "pl-[220px]"
      }`}
    >
      <ProjectGlobalSidebar
        activeTab={currentTab}
        setSidebarCollapsed={setSidebarCollapsed}
      />
      <Header
        organization_name={organization?.organization_name}
        setCurrentTab={handleTabChange}
        currentTab={currentTab}
        active={active}
        flush={flush}
        todayMs={todayMs}
        prepareForLogout={prepareForLogout}
      />
      <div className="flex flex-row justify-start overflow-hidden">
        <div className="h-[90vh] flex-1 overflow-y-scroll mt-1 mx-2 rounded-xl bg-[#F9FAFC]" style={{
          scrollbarWidth: "none"
        }}>
          {currentTab === "Dashboard" ? (
            <>
              {canAccessDashboard ? (
                <div
                  className="flex flex-col h-full"
                >
                  {/* <MainContent
                    filteredSites={filteredSites}
                    onOrganizationClick={onOrganizationClick}
                    organizationId={organization?.id}
                    onNewProjectClick={handleNewProjectClick}
                    isOwner={isOwner}
                  /> */}
                  <OwnerDashboard hasNoOrganization={hasNoOrganization} />
                </div>
              ) : (
                // <div className="h-full w-full flex items-center justify-center px-6 text-center">
                //   <p className="text-sm text-gray-600">
                //     You do not have access to this dashboard.
                //   </p>
                // </div>
                <IndividualDashboard hasNoOrganization={hasNoOrganization} />
              )}
              {/* {isOwner && isValidSubscription && (
              <div className="flex flex-col h-[90vh] w-[30%] overflow-y-auto  px-3 items-center justify-center">
                <RightSidebar />
              </div>
            )} */}
            </>
          ) : currentTab === "Organization" ? (
            <OrganizationDetails
              handleBackClick={() => handleTabChange("Dashboard")}
            />
          ) : currentTab === "Projects" ? (
            <Projects
              organizationId={organization?.id}
              currentMember={currentMember}
            />
          ) : currentTab === "Procurement" ? (
            <Procurement
              currentMember={currentMember}
              permissionsLoaded={permissionsLoaded}
            />
          ) : currentTab === "Warehouse" ? (
            <Warehouse
              currentMember={currentMember}
              permissionsLoaded={permissionsLoaded}
            />
          ) : currentTab === "Vendor" ? (
            <Vendors
              currentMember={currentMember}
              permissionsLoaded={permissionsLoaded}
            />
          ) : currentTab === "Contractor" ? (
            isContractorDetail ? (
              <Outlet />
            ) : (
              <ContractorManagement
                currentMember={currentMember}
                permissionsLoaded={permissionsLoaded}
              />
              // <Contractors currentMember={currentMember} />
            )
          ) : currentTab === "Finance" ? (
            <Finance
              currentMember={currentMember}
              permissionsLoaded={permissionsLoaded}
            />
          ) : currentTab === "Organization Team" ? (
            <OrganizationTeam currentMember={currentMember} />
          ) : currentTab === "Document Manager" ? (
            <div className="w-full h-full overflow-hidden">
              <Outlet />
            </div>
          ) : currentTab === "Raise Request" ? (
            <RaiseRequest />
          ) : currentTab === "Global Communication" ? (
            <Chats />
          ) : currentTab === "Global Media" ? (
            <GlobalMedia id="general-updates" />
          ) : currentTab === "Lead Management" ? (
            <LeadManagement
              currentMember={currentMember}
              permissionsLoaded={permissionsLoaded}
            />
          ) : currentTab === "Design" ? (
            <Design
              currentMember={currentMember}
              permissionsLoaded={permissionsLoaded}
            />
          ) : currentTab === "Petty Cash" ? (
            <PettyCashManagement
              currentMember={currentMember}
              permissionsLoaded={permissionsLoaded}
            />
          ) : currentTab === "Snags" ? (
            <SnagsManagement
              organizationId={organization?.id}
              currentMember={currentMember}
              permissionsLoaded={permissionsLoaded}
            />
          ) : currentTab === "Actions" ? (
            <Manage
              organizationId={organization?.id}
              currentMember={currentMember}
              isOwner={isOwner}
            />
          ) : currentTab === "Settings" ? (
            <Settings currentMember={currentMember} />
          ) : currentTab === "Help" ? (
            <HelpPage />
          ) : currentTab === "Global Report" ? (
            <GlobalReport organizationId={organization?.id} currentMember={currentMember} permissionsLoaded={permissionsLoaded} />
          ) : (
            <div className="w-[85%] py-2 ml-2 h-[90vh] flex flex-col items-center justify-center">
              <ComingSoonCard
                stage={
                  currentTab === "Central Inventory"
                    ? 4
                    : currentTab === "Vendor"
                      ? 4
                      : currentTab === "Contractor"
                        ? 5
                        : currentTab === "Finance"
                          ? 3
                          : 1
                }
                featureName={currentTab || "Coming Soon"}
                description={
                  currentTab === "Central Inventory"
                    ? "Manage your inventory with ease."
                    : currentTab === "Vendor"
                      ? "Find and manage vendors."
                      : currentTab === "Contractor"
                        ? "Manage contractors efficiently."
                        : currentTab === "Finance"
                          ? "Keep track of your organization finances."
                          : "Coming Soon"
                }
              />
            </div>
          )}
        </div>
        <Modal isOpen={showNewSiteForm} onClose={handleCloseNewSiteForm}>
          <NewSiteForm onClose={handleCloseNewSiteForm} />
        </Modal>

        <Modal
          isOpen={showInvitationsForm}
          onClose={handleCloseInvitationsForm}
        >
          <AddNewMember
            onClose={handleCloseInvitationsForm}
            organizationId={organization?.id}
          />
        </Modal>
      </div>
    </div>
  );
}

export default Dashboard;
